import { useRef, useState } from "react";
import { Button } from "antd";
import { Input } from "@components/global/new/Input/Input";
import { useIntl } from "react-intl";
import ReCAPTCHA from "react-google-recaptcha";
import cn from "classnames";
import { IS_DEBUG, REACT_RECAPTCH_KEY } from "../../../../config";
import { wlpFormsApi } from "../../utils";
import useMediaQuery from "@utils/hooks/utils";
import { useWlpFormStatus } from "../../hooks/useWlpFormStatus";
import { WlpFormStatusIcon } from "../WlpFormStatusIcon/WlpFormStatusIcon";
import styles from "./SubscribeCard.module.scss";

interface SubscribeElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
}

interface SubscribeForm extends HTMLFormElement {
  readonly elements: SubscribeElements;
}

interface SubscribeErrors {
  email?: string;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SubscribeCard() {
  const { formatMessage } = useIntl();
  const recaptchaRef = useRef<any>(null);
  const isXs = useMediaQuery("max", 574);
  const isNarrowPhone = useMediaQuery("max", 370);
  const [recaptchaResponse, setRecaptchaResponse] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<SubscribeErrors>({});
  const formStatus = useWlpFormStatus();

  const clearEmailError = () => {
    formStatus.clearStatus();
    setErrors((prev) => {
      if (!prev.email) return prev;
      return { ...prev, email: undefined };
    });
  };

  const onSubmit = async (e: React.FormEvent<SubscribeForm>) => {
    e.preventDefault();
    if (loading) return;
    formStatus.clearStatus();

    const form = e.currentTarget;
    const email = form.elements.email.value.trim();
    const recaptcha = IS_DEBUG ? "fakerecaptcha" : recaptchaResponse;
    const nextErrors: SubscribeErrors = {};

    if (!email) {
      nextErrors.email = formatMessage({ id: "wlp.form.emailRequired" });
    }

    if (email && !emailPattern.test(email)) {
      nextErrors.email = formatMessage({ id: "wlp.form.emailInvalid" });
    }

    setErrors(nextErrors);

    if (nextErrors.email) {
      return;
    }

    if (!recaptcha) {
      formStatus.showError(formatMessage({ id: "wlp.form.recaptchaRequired" }));
      return;
    }

    setLoading(true);

    try {
      const response = await wlpFormsApi.subscribe(
        {
          email,
          name: "empty",
        },
        recaptcha
      );

      if (response.Status === "error") {
        formStatus.showError(
          response.Message || formatMessage({ id: "wlp.form.genericError" })
        );
        return;
      }

      form.reset();
      recaptchaRef.current?.reset();
      setRecaptchaResponse(undefined);
      setErrors({});
      formStatus.showSuccess(formatMessage({ id: "wlp.subscribe.success" }));
    } catch {
      formStatus.showError(formatMessage({ id: "wlp.form.genericError" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.card}>
      <h2 className={styles.title}>{formatMessage({ id: "wlp.subscribe.title" })}</h2>
      <form className={styles.form} onSubmit={onSubmit}>
        <Input
          label={formatMessage({ id: "wlp.subscribe.emailLabel" })}
          name="email"
          type="email"
          placeholder={formatMessage({ id: "wlp.subscribe.emailPlaceholder" })}
          size="large"
          colored
          outlined
          wrapClassName={styles.field}
          onChange={clearEmailError}
        />
        {errors.email && <span className={styles.error}>{errors.email}</span>}
        {REACT_RECAPTCH_KEY && !IS_DEBUG && (
          <div
            className={cn("g-recaptcha", styles.recaptcha, {
              [styles.xsRecaptcha]: isXs,
              [styles.compactRecaptcha]: isNarrowPhone,
            })}
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={REACT_RECAPTCH_KEY}
              size={isNarrowPhone ? "compact" : "normal"}
              onChange={(response) => setRecaptchaResponse(response || undefined)}
              onExpired={() => setRecaptchaResponse(undefined)}
            />
          </div>
        )}
        <Button
          type="primary"
          htmlType="submit"
          className={styles.submit}
          loading={loading}
          disabled={REACT_RECAPTCH_KEY && !IS_DEBUG ? !recaptchaResponse : false}
        >
          {formatMessage({ id: "wlp.subscribe.button" })}
        </Button>
        <p
          className={cn(styles.status, {
            [styles.statusVisible]: formStatus.status?.visible,
            [styles.statusSuccess]: formStatus.status?.type === "success",
            [styles.statusError]: formStatus.status?.type === "error",
          })}
          aria-live={formStatus.status?.type === "error" ? "assertive" : "polite"}
        >
          {formStatus.status && <WlpFormStatusIcon type={formStatus.status.type} />}
          <span>{formStatus.status?.message}</span>
        </p>
      </form>
    </section>
  );
}

export { SubscribeCard };
